import React, {FC} from 'react';

import {defineMessage} from '../../../../libs/shared/helpers/src/internationalization/message';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {VisualWorkspaceDelegation, VisualWorkspaceModel} from '../../../models/api/visualWorkspaceModel';
import {getHasLenses} from '../../../store/featureFlags/featureFlagsSelectors';
import {InboxesPanelId} from '../../../store/panels/panelStateBase';
import {LeftPanel} from '../../app/leftPanel';
import {LeftPanelTopbar} from '../../app/leftPanelTopbar/leftPanelTopbar';
import {ComposeButtonContainer} from './composeButtonContainer';
import {MaybeDelegationWorkspaceContainer} from './delegationWorkspace/maybeDelegationWorkspaceContainer';
import {WorkspaceDragSourceTypesEnum} from './dnd/workspaceDragDropTypes';
import {WorkspacesAutoRecBannerContainer} from './editor/workspacesAutoRecBannerContainer';
import {LensesWorkspaceContainer} from './lensesWorkspace/lensesWorkspaceContainer';
import {TeammatesWorkspaceContainer} from './teammatesWorkspaceContainer';
import {TeammateWorkspaceContainer} from './teammateWorkspace/teammateWorkspaceContainer';
import {MaybeTeamWorkspaceContainer} from './teamWorkspace/maybeTeamWorkspaceContainer';

/*
 * Props.
 */

export interface WorkspacesProps {
  visualWorkspace: VisualWorkspaceModel;
  panelId: InboxesPanelId;
  /** The ID of the currently selected inbox, if any. */
  selectedInboxId?: number;
  /** Width of the panel. */
  size: number;
  hasP1AutoRec: boolean;
  shouldShowP1AutoRecBanner: boolean;
  maybeUpdatePreferencesAndWorkspace: Handler;
  shouldHideAssignedConversations: boolean;
}

/*
 * Component.
 */

export const Workspaces: FC<WorkspacesProps> = (props) => {
  const {panelId, visualWorkspace, maybeUpdatePreferencesAndWorkspace, shouldHideAssignedConversations} =
    props;

  const localize = useLocalize();
  const hasLenses = useAppSelector(getHasLenses);

  useInitialEffect(() => {
    maybeUpdatePreferencesAndWorkspace();
  });

  return (
    <LeftPanel panelId={panelId} interactionId="workspaces" topBar={renderLeftPanelTopbar(props)}>
      <nav
        aria-label={localize(
          defineMessage({
            id: 'workspaces-region',
            message: 'Workspaces',
          }),
        )}
      >
        <TeammateWorkspaceContainer
          panelId={panelId}
          visualWorkspaceRootFolders={visualWorkspace.rootFolders}
          visualWorkspaceNestedFolders={visualWorkspace.nestedFolders}
          visualWorkspaceTags={visualWorkspace.teammateWorkspaceTags}
        />
        {hasLenses && (
          <LensesWorkspaceContainer
            panelId={panelId}
            visualWorkspaceLenses={visualWorkspace.lenses.items}
            isHidden={visualWorkspace.lenses.isHidden}
          />
        )}
        {renderVisualWorkspaceTeams(panelId, visualWorkspace)}
        {renderDelegations(panelId, visualWorkspace.delegations)}
        {!shouldHideAssignedConversations && !visualWorkspace.teammates.isHidden && (
          <TeammatesWorkspaceContainer
            panelId={panelId}
            visualWorkspaceTeammates={visualWorkspace.teammates.items}
          />
        )}
      </nav>
    </LeftPanel>
  );
};

/*
 * Helpers.
 */

function renderLeftPanelTopbar(props: WorkspacesProps) {
  return (
    <LeftPanelTopbar>
      <ComposeButtonContainer size={props.size} selectedInboxId={props.selectedInboxId} />
      {props.hasP1AutoRec && props.shouldShowP1AutoRecBanner && (
        <WorkspacesAutoRecBannerContainer panelId={props.panelId} />
      )}
    </LeftPanelTopbar>
  );
}

function renderVisualWorkspaceTeams(panelId: InboxesPanelId, visualWorkspace: VisualWorkspaceModel) {
  return visualWorkspace.teams.map((teamData, index) => (
    <MaybeTeamWorkspaceContainer
      key={teamData.teamId}
      visualWorkspaceTeam={teamData}
      panelId={panelId}
      isFirstTeam={index === 0}
    />
  ));
}

function renderDelegations(
  panelId: InboxesPanelId,
  visualWorkspaceDelegations: ReadonlyArray<VisualWorkspaceDelegation>,
) {
  return visualWorkspaceDelegations
    .filter(({isSelected}) => isSelected)
    .map(({id}) => (
      <MaybeDelegationWorkspaceContainer
        key={id}
        panelId={panelId}
        delegationId={id}
        workspaceDragItem={{
          type: WorkspaceDragSourceTypesEnum.DELEGATION,
          id,
        }}
      />
    ));
}
